import React, { useEffect, useState, memo } from 'react';
import { RefreshCwIcon, ChevronDownIcon, ClockIcon, ServerIcon, CpuIcon, MemoryStickIcon, NetworkIcon, ActivityIcon, DownloadIcon, AlertCircleIcon, CheckCircleIcon, XCircleIcon, HelpCircleIcon, FilterIcon, BarChart4Icon, PieChartIcon, DatabaseIcon, LayersIcon, GaugeIcon, LineChartIcon, InfoIcon, ArrowUpIcon, ArrowDownIcon, LayoutDashboardIcon, ShieldIcon, AlertTriangleIcon, BellIcon, UsersIcon, ListIcon, SettingsIcon, HistoryIcon, TrendingUpIcon, MoveHorizontalIcon } from 'lucide-react';
import { ProgressBar } from '../components/ProgressBar';
import { LineChart } from '../components/LineChart';
import { GaugeChart } from '../components/GaugeChart';
export function Realtime() {
  const [selectedServer, setSelectedServer] = useState('all');
  const [timeRange, setTimeRange] = useState('1h');
  const [refreshInterval, setRefreshInterval] = useState(30);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState('system');
  const [selectedChartType, setSelectedChartType] = useState('line');
  const [activeTab, setActiveTab] = useState('overview');
  const [showRefreshOptions, setShowRefreshOptions] = useState(false);
  const [showServerOptions, setShowServerOptions] = useState(false);
  // Mock data for time series
  const generateTimeData = (count: number, base: number, variance: number) => {
    const now = new Date();
    const data = [];
    for (let i = count - 1; i >= 0; i--) {
      const time = new Date(now.getTime() - i * 60000);
      const value = Math.max(0, Math.min(100, base + (Math.random() * variance * 2 - variance)));
      data.push({
        value,
        label: `${time.getHours()}:${time.getMinutes().toString().padStart(2, '0')}`
      });
    }
    return data;
  };
  const systemData = generateTimeData(60, 65, 10);
  const cpuData = generateTimeData(60, 45, 15);
  const memoryData = generateTimeData(60, 62, 8);
  const gpuData = generateTimeData(60, 78, 20);
  const networkData = generateTimeData(60, 35, 25);
  // Simulate refresh
  const handleRefresh = () => {
    setIsRefreshing(true);
    setTimeout(() => {
      setIsRefreshing(false);
    }, 1000);
  };
  // Auto refresh based on interval
  useEffect(() => {
    const timer = setInterval(() => {
      handleRefresh();
    }, refreshInterval * 1000);
    return () => clearInterval(timer);
  }, [refreshInterval]);
  // Mock data for alerts
  const alerts = [{
    id: 1,
    message: 'GPU 2 temperature exceeding threshold',
    timestamp: '2 min ago',
    severity: 'critical'
  }, {
    id: 2,
    message: 'API rate limit reached for key ABC123',
    timestamp: '15 min ago',
    severity: 'warning'
  }, {
    id: 3,
    message: 'Model swap completed: Llama-4-8B',
    timestamp: '45 min ago',
    severity: 'info'
  }];
  // Mock data for team/project usage
  const teamUsage = [{
    id: 1,
    name: 'Engineering',
    usage: 42,
    quota: 100
  }, {
    id: 2,
    name: 'Research',
    usage: 78,
    quota: 100
  }, {
    id: 3,
    name: 'Marketing',
    usage: 23,
    quota: 50
  }, {
    id: 4,
    name: 'Support',
    usage: 67,
    quota: 80
  }];
  // Service distribution data
  const serviceData = [{
    name: 'Llama-4-8B',
    value: 35,
    color: '#3b82f6'
  }, {
    name: 'DeepSeek-V3',
    value: 25,
    color: '#8b5cf6'
  }, {
    name: 'Gemma-3',
    value: 20,
    color: '#10b981'
  }, {
    name: 'Mistral-3',
    value: 15,
    color: '#f59e0b'
  }, {
    name: 'Others',
    value: 5,
    color: '#6b7280'
  }];
  // Infrastructure SLO data
  const infrastructureSLO = [{
    name: 'GPU Cluster 1',
    current: 78,
    target: 85,
    status: 'good'
  }, {
    name: 'CPU Cluster',
    current: 65,
    target: 75,
    status: 'good'
  }, {
    name: 'Memory Pool',
    current: 82,
    target: 80,
    status: 'warning'
  }, {
    name: 'Network',
    current: 45,
    target: 90,
    status: 'good'
  }, {
    name: 'Storage',
    current: 92,
    target: 90,
    status: 'critical'
  }];
  // API request distribution
  const apiRequestData = [{
    name: 'Chat Completions',
    value: 45,
    color: '#3b82f6'
  }, {
    name: 'Text Embeddings',
    value: 30,
    color: '#8b5cf6'
  }, {
    name: 'Image Generation',
    value: 15,
    color: '#10b981'
  }, {
    name: 'Text Completions',
    value: 10,
    color: '#f59e0b'
  }];
  // Get status color
  const getStatusColor = status => {
    switch (status) {
      case 'good':
        return 'text-green-400';
      case 'warning':
        return 'text-yellow-400';
      case 'critical':
        return 'text-red-400';
      default:
        return 'text-gray-400';
    }
  };
  // Tab definitions
  const tabs = [{
    id: 'overview',
    label: 'Overview',
    icon: <LayoutDashboardIcon size={18} data-id="element-1920" />
  }, {
    id: 'infrastructure',
    label: 'Infrastructure',
    icon: <ServerIcon size={18} data-id="element-1921" />
  }, {
    id: 'models',
    label: 'Models',
    icon: <DatabaseIcon size={18} data-id="element-1922" />
  }, {
    id: 'api',
    label: 'API',
    icon: <ActivityIcon size={18} data-id="element-1923" />
  }, {
    id: 'alerts',
    label: 'Alerts & Logs',
    icon: <BellIcon size={18} data-id="element-1924" />
  }];
  return <div className="space-y-6 bg-black text-white" data-id="element-1925">
      {/* Header with controls */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 animate-fadeIn" data-id="element-1926">
        <div className="flex items-center gap-2" data-id="element-1927">
          <h2 className="text-xl font-bold text-white flex items-center gap-2" data-id="element-1928">
            <TrendingUpIcon size={20} className="text-blue-400" data-id="element-1929" />
            System Monitoring
            <span className="ml-2 px-2 py-0.5 bg-green-900/40 text-green-400 text-xs rounded-full border border-green-800/50 flex items-center" data-id="element-1930">
              <span className="h-1.5 w-1.5 bg-green-400 rounded-full mr-1.5 animate-pulse" data-id="element-1931"></span>
              Live
            </span>
          </h2>
        </div>
        <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap" data-id="element-1932">
          {/* Time Range Selector */}
          <div className="bg-[#121212] rounded-lg border border-[#333] overflow-hidden flex shadow-inner" data-id="element-1933">
            <button className={`px-3 py-1.5 text-sm ${timeRange === '1h' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-[#1a1a1a]'}`} onClick={() => setTimeRange('1h')} data-id="element-1934">
              1h
            </button>
            <button className={`px-3 py-1.5 text-sm ${timeRange === '6h' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-[#1a1a1a]'}`} onClick={() => setTimeRange('6h')} data-id="element-1935">
              6h
            </button>
            <button className={`px-3 py-1.5 text-sm ${timeRange === '24h' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-[#1a1a1a]'}`} onClick={() => setTimeRange('24h')} data-id="element-1936">
              24h
            </button>
            <button className={`px-3 py-1.5 text-sm ${timeRange === '7d' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-[#1a1a1a]'}`} onClick={() => setTimeRange('7d')} data-id="element-1937">
              7d
            </button>
          </div>
          {/* Server Selector */}
          <div className="relative" data-id="element-1938">
            <button className="flex items-center gap-2 bg-[#121212] text-gray-100 px-3 py-1.5 rounded-lg border border-[#333] hover:bg-[#1a1a1a] transition-colors text-sm" onClick={() => setShowServerOptions(!showServerOptions)} data-id="element-1939">
              <ServerIcon size={16} data-id="element-1940" />
              <span data-id="element-1941">
                {selectedServer === 'all' ? 'All Servers' : selectedServer}
              </span>
              <ChevronDownIcon size={16} data-id="element-1942" />
            </button>
            {showServerOptions && <div className="absolute right-0 mt-1 w-48 bg-[#121212] border border-[#333] rounded-lg shadow-lg z-10 py-1" data-id="element-1943">
                <button className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-[#1a1a1a]" onClick={() => {
              setSelectedServer('all');
              setShowServerOptions(false);
            }} data-id="element-1944">
                  All Servers
                </button>
                <button className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-[#1a1a1a]" onClick={() => {
              setSelectedServer('GPU Cluster 1');
              setShowServerOptions(false);
            }} data-id="element-1945">
                  GPU Cluster 1
                </button>
                <button className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-[#1a1a1a]" onClick={() => {
              setSelectedServer('CPU Cluster');
              setShowServerOptions(false);
            }} data-id="element-1946">
                  CPU Cluster
                </button>
                <button className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-[#1a1a1a]" onClick={() => {
              setSelectedServer('Memory Pool');
              setShowServerOptions(false);
            }} data-id="element-1947">
                  Memory Pool
                </button>
              </div>}
          </div>
          {/* Refresh Controls */}
          <div className="relative" data-id="element-1948">
            <button className={`p-2 rounded-lg ${isRefreshing ? 'bg-blue-600 text-white' : 'bg-[#121212] text-gray-100 hover:bg-[#1a1a1a] border border-[#333]'} transition-colors`} onClick={() => setShowRefreshOptions(!showRefreshOptions)} data-id="element-1949">
              <RefreshCwIcon size={18} className={isRefreshing ? 'animate-spin' : ''} data-id="element-1950" />
            </button>
            {showRefreshOptions && <div className="absolute right-0 mt-1 w-48 bg-[#121212] border border-[#333] rounded-lg shadow-lg z-10 py-1" data-id="element-1951">
                <div className="px-3 py-2 text-xs text-gray-400 border-b border-[#333]" data-id="element-1952">
                  Auto-refresh interval
                </div>
                {[10, 30, 60, 300].map(seconds => <button key={seconds} className={`w-full text-left px-4 py-2 text-sm ${refreshInterval === seconds ? 'text-blue-400 bg-[#1a1a1a]' : 'text-gray-300 hover:bg-[#1a1a1a]'}`} onClick={() => {
              setRefreshInterval(seconds);
              setShowRefreshOptions(false);
            }} data-id="element-1953">
                    {seconds < 60 ? `${seconds} seconds` : `${seconds / 60} minutes`}
                  </button>)}
                <div className="border-t border-[#333] mt-1 pt-1" data-id="element-1954">
                  <button className="w-full text-left px-4 py-2 text-sm text-blue-400 hover:bg-[#1a1a1a] flex items-center gap-2" onClick={() => {
                handleRefresh();
                setShowRefreshOptions(false);
              }} data-id="element-1955">
                    <RefreshCwIcon size={14} data-id="element-1956" />
                    Refresh now
                  </button>
                </div>
              </div>}
          </div>
        </div>
      </div>
      {/* Tab Navigation */}
      <div className="border-b border-[#222] mb-6" data-id="element-1957">
        <div className="flex overflow-x-auto hide-scrollbar" data-id="element-1958">
          {tabs.map(tab => <button key={tab.id} className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${activeTab === tab.id ? 'text-white border-b-2 border-blue-500' : 'text-gray-400 hover:text-gray-200'}`} onClick={() => setActiveTab(tab.id)} data-id="element-1959">
              {tab.icon}
              {tab.label}
            </button>)}
        </div>
      </div>
      {/* Tab Content */}
      <div className="animate-fadeIn" data-id="element-1960">
        {/* OVERVIEW TAB */}
        {activeTab === 'overview' && <div className="space-y-6" data-id="element-1961">
            {/* Summary Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-1962">
              <div className="bg-[#0F0F0F] rounded-xl p-4 border border-[#222] transition-all duration-300 hover:border-[#333] hover:shadow-lg" data-id="element-1963">
                <div className="flex items-center justify-between" data-id="element-1964">
                  <h3 className="text-sm font-medium text-gray-400 flex items-center" data-id="element-1965">
                    <CpuIcon size={16} className="mr-2 text-blue-400" data-id="element-1966" /> CPU
                    Usage
                  </h3>
                  <span className="text-xl font-semibold text-white" data-id="element-1967">45%</span>
                </div>
                <div className="mt-2" data-id="element-1968">
                  <ProgressBar value={45} max={100} color="blue" size="md" data-id="element-1969" />
                </div>
                <div className="mt-2 text-xs text-gray-500" data-id="element-1970">
                  <span className="text-green-400 flex items-center gap-1" data-id="element-1971">
                    <ArrowDownIcon size={12} data-id="element-1972" />
                    5% from last hour
                  </span>
                </div>
              </div>
              <div className="bg-[#0F0F0F] rounded-xl p-4 border border-[#222] transition-all duration-300 hover:border-[#333] hover:shadow-lg" data-id="element-1973">
                <div className="flex items-center justify-between" data-id="element-1974">
                  <h3 className="text-sm font-medium text-gray-400 flex items-center" data-id="element-1975">
                    <MemoryStickIcon size={16} className="mr-2 text-green-400" data-id="element-1976" />{' '}
                    Memory Usage
                  </h3>
                  <span className="text-xl font-semibold text-white" data-id="element-1977">62%</span>
                </div>
                <div className="mt-2" data-id="element-1978">
                  <ProgressBar value={62} max={100} color="green" size="md" data-id="element-1979" />
                </div>
                <div className="mt-2 text-xs text-gray-500" data-id="element-1980">
                  <span className="text-red-400 flex items-center gap-1" data-id="element-1981">
                    <ArrowUpIcon size={12} data-id="element-1982" />
                    8% from last hour
                  </span>
                </div>
              </div>
              <div className="bg-[#0F0F0F] rounded-xl p-4 border border-[#222] transition-all duration-300 hover:border-[#333] hover:shadow-lg" data-id="element-1983">
                <div className="flex items-center justify-between" data-id="element-1984">
                  <h3 className="text-sm font-medium text-gray-400 flex items-center" data-id="element-1985">
                    <ServerIcon size={16} className="mr-2 text-purple-400" data-id="element-1986" />{' '}
                    GPU Usage
                  </h3>
                  <span className="text-xl font-semibold text-white" data-id="element-1987">78%</span>
                </div>
                <div className="mt-2" data-id="element-1988">
                  <ProgressBar value={78} max={100} color="purple" size="md" data-id="element-1989" />
                </div>
                <div className="mt-2 text-xs text-gray-500" data-id="element-1990">
                  <span className="text-yellow-400 flex items-center gap-1" data-id="element-1991">
                    <MoveHorizontalIcon size={12} data-id="element-1992" />
                    No change from last hour
                  </span>
                </div>
              </div>
              <div className="bg-[#0F0F0F] rounded-xl p-4 border border-[#222] transition-all duration-300 hover:border-[#333] hover:shadow-lg" data-id="element-1993">
                <div className="flex items-center justify-between" data-id="element-1994">
                  <h3 className="text-sm font-medium text-gray-400 flex items-center" data-id="element-1995">
                    <NetworkIcon size={16} className="mr-2 text-yellow-400" data-id="element-1996" />{' '}
                    Network I/O
                  </h3>
                  <span className="text-xl font-semibold text-white" data-id="element-1997">
                    35 MB/s
                  </span>
                </div>
                <div className="mt-2" data-id="element-1998">
                  <ProgressBar value={35} max={100} color="yellow" size="md" data-id="element-1999" />
                </div>
                <div className="mt-2 text-xs text-gray-500" data-id="element-2000">
                  <span className="text-green-400 flex items-center gap-1" data-id="element-2001">
                    <ArrowDownIcon size={12} data-id="element-2002" />
                    12% from last hour
                  </span>
                </div>
              </div>
            </div>
            {/* Overview Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-2003">
              {/* System Health */}
              <div className="bg-[#0F0F0F] rounded-xl p-4 border border-[#222]" data-id="element-2004">
                <h3 className="text-base font-medium text-white mb-4 flex items-center gap-2" data-id="element-2005">
                  <CheckCircleIcon size={16} className="text-green-400" data-id="element-2006" />
                  System Health
                </h3>
                <div className="space-y-4" data-id="element-2007">
                  <div className="flex items-center justify-between" data-id="element-2008">
                    <div className="flex items-center" data-id="element-2009">
                      <CheckCircleIcon size={16} className="text-green-500 mr-2" data-id="element-2010" />
                      <span className="text-sm text-gray-300" data-id="element-2011">Uptime</span>
                    </div>
                    <span className="text-sm font-medium text-green-400" data-id="element-2012">
                      99.98%
                    </span>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-2013">
                    <div className="flex items-center" data-id="element-2014">
                      <AlertCircleIcon size={16} className="text-yellow-500 mr-2" data-id="element-2015" />
                      <span className="text-sm text-gray-300" data-id="element-2016">
                        Response Time
                      </span>
                    </div>
                    <span className="text-sm font-medium text-yellow-400" data-id="element-2017">
                      120ms
                    </span>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-2018">
                    <div className="flex items-center" data-id="element-2019">
                      <CheckCircleIcon size={16} className="text-green-500 mr-2" data-id="element-2020" />
                      <span className="text-sm text-gray-300" data-id="element-2021">Error Rate</span>
                    </div>
                    <span className="text-sm font-medium text-green-400" data-id="element-2022">
                      0.02%
                    </span>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-2023">
                    <div className="flex items-center" data-id="element-2024">
                      <HelpCircleIcon size={16} className="text-blue-500 mr-2" data-id="element-2025" />
                      <span className="text-sm text-gray-300" data-id="element-2026">
                        Model Status
                      </span>
                    </div>
                    <span className="text-sm font-medium text-blue-400" data-id="element-2027">
                      Healthy
                    </span>
                  </div>
                </div>
              </div>
              {/* Active Models */}
              <div className="bg-[#0F0F0F] rounded-xl p-4 border border-[#222]" data-id="element-2028">
                <h3 className="text-base font-medium text-white mb-4 flex items-center gap-2" data-id="element-2029">
                  <DatabaseIcon size={16} className="text-blue-400" data-id="element-2030" />
                  Active Models
                </h3>
                <div className="space-y-3" data-id="element-2031">
                  {/* SaaS Models */}
                  <h4 className="text-sm font-medium text-gray-200 mb-3" data-id="element-2032">
                    SaaS
                  </h4>
                  <div className="flex items-center justify-between" data-id="element-2033">
                    <div className="flex items-center" data-id="element-2034">
                      <div className="h-2 w-2 bg-emerald-500 rounded-full mr-2" data-id="element-2035"></div>
                      <span className="text-sm text-gray-300" data-id="element-2036">GPT-4o</span>
                    </div>
                    <span className="text-sm font-medium text-gray-200" data-id="element-2037">
                      31%
                    </span>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-2038">
                    <div className="flex items-center" data-id="element-2039">
                      <div className="h-2 w-2 bg-emerald-500 rounded-full mr-2" data-id="element-2040"></div>
                      <span className="text-sm text-gray-300" data-id="element-2041">Claude-3.5</span>
                    </div>
                    <span className="text-sm font-medium text-gray-200" data-id="element-2042">
                      22%
                    </span>
                  </div>
                  {/* Self-Deployed Models */}
                  <h4 className="text-sm font-medium text-gray-200 mt-5 mb-3 flex items-center" data-id="element-2043">
                    Self-Deployed
                    <span className="ml-2 text-amber-400" data-id="element-2044">
                      <AlertTriangleIcon size={14} data-id="element-2045" />
                    </span>
                  </h4>
                  <div className="flex items-center justify-between" data-id="element-2046">
                    <div className="flex items-center" data-id="element-2047">
                      <div className="h-2 w-2 bg-blue-500 rounded-full mr-2" data-id="element-2048"></div>
                      <span className="text-sm text-gray-300" data-id="element-2049">
                        Qwen 2.5 1.5B Instruct
                      </span>
                    </div>
                    <span className="text-sm font-medium text-gray-200" data-id="element-2050">
                      19%
                    </span>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-2051">
                    <div className="flex items-center" data-id="element-2052">
                      <div className="h-2 w-2 bg-blue-500 rounded-full mr-2" data-id="element-2053"></div>
                      <span className="text-sm text-gray-300" data-id="element-2054">
                        DeepSeek V3.1
                      </span>
                    </div>
                    <span className="text-sm font-medium text-gray-200" data-id="element-2055">
                      27%
                    </span>
                  </div>
                </div>
              </div>
              {/* Recent Alerts */}
              <div className="bg-[#0F0F0F] rounded-xl p-4 border border-[#222]" data-id="element-2056">
                <h3 className="text-base font-medium text-white mb-4 flex items-center gap-2" data-id="element-2057">
                  <BellIcon size={16} className="text-red-400" data-id="element-2058" />
                  Recent Alerts
                </h3>
                <div className="space-y-3" data-id="element-2059">
                  {alerts.map(alert => <div key={alert.id} className="flex items-start" data-id="element-2060">
                      <div className={`mt-1 h-2 w-2 rounded-full mr-3 ${alert.severity === 'critical' ? 'bg-red-500' : alert.severity === 'warning' ? 'bg-yellow-500' : 'bg-blue-500'}`} data-id="element-2061"></div>
                      <div className="flex-1" data-id="element-2062">
                        <p className="text-sm text-gray-200" data-id="element-2063">{alert.message}</p>
                        <p className="text-xs text-gray-500" data-id="element-2064">
                          {alert.timestamp}
                        </p>
                      </div>
                    </div>)}
                </div>
              </div>
            </div>
            {/* API Request Distribution */}
            <div className="bg-[#0F0F0F] rounded-xl p-4 border border-[#222]" data-id="element-2065">
              <h3 className="text-base font-medium text-white mb-4 flex items-center gap-2" data-id="element-2066">
                <ActivityIcon size={16} className="text-blue-400" data-id="element-2067" />
                API Request Distribution
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-2068">
                {apiRequestData.map((api, index) => <div key={index} className="bg-[#121212] p-3 rounded-lg border border-[#333]" data-id="element-2069">
                    <div className="flex items-center mb-2" data-id="element-2070">
                      <div className="h-3 w-3 rounded-full mr-2" style={{
                  backgroundColor: api.color
                }} data-id="element-2071"></div>
                      <h4 className="text-sm font-medium text-gray-300" data-id="element-2072">
                        {api.name}
                      </h4>
                    </div>
                    <div className="text-xl font-bold text-white mb-1" data-id="element-2073">
                      {api.value}%
                    </div>
                    <ProgressBar value={api.value} max={100} color={api.color} size="sm" data-id="element-2074" />
                  </div>)}
              </div>
            </div>
            {/* Model Usage */}
            <div className="bg-[#0F0F0F] rounded-xl p-4 border border-[#222]" data-id="element-2075">
              <h3 className="text-base font-medium text-white mb-4 flex items-center gap-2" data-id="element-2076">
                <DatabaseIcon size={16} className="text-indigo-400" data-id="element-2077" />
                Model Usage Distribution
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4" data-id="element-2078">
                {serviceData.map((service, index) => <div key={index} className="bg-[#121212] p-3 rounded-lg border border-[#333]" data-id="element-2079">
                    <div className="flex items-center mb-2" data-id="element-2080">
                      <div className="h-3 w-3 rounded-full mr-2" style={{
                  backgroundColor: service.color
                }} data-id="element-2081"></div>
                      <h4 className="text-sm font-medium text-gray-300" data-id="element-2082">
                        {service.name}
                      </h4>
                    </div>
                    <div className="text-xl font-bold text-white mb-1" data-id="element-2083">
                      {service.value}%
                    </div>
                    <ProgressBar value={service.value} max={100} color={service.color} size="sm" data-id="element-2084" />
                  </div>)}
              </div>
            </div>
          </div>}
        {/* INFRASTRUCTURE TAB */}
        {activeTab === 'infrastructure' && <div className="space-y-6" data-id="element-2085">
            {/* Infrastructure SLO Utilization */}
            <div className="bg-[#0F0F0F] rounded-xl p-5 border border-[#222]" data-id="element-2086">
              <div className="flex items-center justify-between mb-4" data-id="element-2087">
                <h3 className="text-lg font-medium text-white flex items-center gap-2" data-id="element-2088">
                  <ServerIcon size={18} className="text-blue-400" data-id="element-2089" />
                  Infrastructure SLO Utilization
                </h3>
                <div className="flex items-center gap-2" data-id="element-2090">
                  <button className="p-1.5 text-gray-400 hover:text-white bg-[#121212] rounded-lg border border-[#333] hover:bg-[#1a1a1a]" data-id="element-2091">
                    <FilterIcon size={16} data-id="element-2092" />
                  </button>
                  <button className="p-1.5 text-gray-400 hover:text-white bg-[#121212] rounded-lg border border-[#333] hover:bg-[#1a1a1a]" data-id="element-2093">
                    <DownloadIcon size={16} data-id="element-2094" />
                  </button>
                </div>
              </div>
              <div className="overflow-x-auto" data-id="element-2095">
                <table className="min-w-full divide-y divide-[#222]" data-id="element-2096">
                  <thead data-id="element-2097">
                    <tr className="bg-[#121212]" data-id="element-2098">
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider" data-id="element-2099">
                        Infrastructure
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider" data-id="element-2100">
                        Current
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider" data-id="element-2101">
                        Target
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider" data-id="element-2102">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider" data-id="element-2103">
                        Utilization
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#222] bg-[#0F0F0F]" data-id="element-2104">
                    {infrastructureSLO.map((item, index) => <tr key={index} className="hover:bg-[#121212] transition-colors" data-id="element-2105">
                        <td className="px-4 py-3 text-sm text-white" data-id="element-2106">
                          {item.name}
                        </td>
                        <td className="px-4 py-3 text-sm text-white" data-id="element-2107">
                          {item.current}%
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-300" data-id="element-2108">
                          {item.target}%
                        </td>
                        <td className="px-4 py-3 text-sm" data-id="element-2109">
                          <span className={`flex items-center ${getStatusColor(item.status)}`} data-id="element-2110">
                            {item.status === 'good' && <CheckCircleIcon size={14} className="mr-1" data-id="element-2111" />}
                            {item.status === 'warning' && <AlertCircleIcon size={14} className="mr-1" data-id="element-2112" />}
                            {item.status === 'critical' && <XCircleIcon size={14} className="mr-1" data-id="element-2113" />}
                            {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-300 w-1/3" data-id="element-2114">
                          <div className="flex items-center" data-id="element-2115">
                            <div className="w-full" data-id="element-2116">
                              <ProgressBar value={item.current} max={100} color={item.status === 'good' ? 'green' : item.status === 'warning' ? 'yellow' : 'red'} size="sm" data-id="element-2117" />
                            </div>
                            <div className="ml-2 whitespace-nowrap" data-id="element-2118">
                              {item.current > item.target ? <span className="text-red-400 flex items-center text-xs" data-id="element-2119">
                                  <ArrowUpIcon size={12} data-id="element-2120" />
                                  {(item.current - item.target).toFixed(1)}%
                                </span> : <span className="text-green-400 flex items-center text-xs" data-id="element-2121">
                                  <ArrowDownIcon size={12} data-id="element-2122" />
                                  {(item.target - item.current).toFixed(1)}%
                                </span>}
                            </div>
                          </div>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </div>
            {/* Performance Metrics Charts */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-2123">
              {/* CPU Performance - Gauge Chart */}
              <div className="bg-[#0F0F0F] rounded-xl p-4 border border-[#222]" data-id="element-2124">
                <div className="flex items-center justify-between mb-2" data-id="element-2125">
                  <h3 className="text-base font-medium text-white flex items-center gap-2" data-id="element-2126">
                    <CpuIcon size={16} className="text-blue-400" data-id="element-2127" /> CPU
                    Performance
                  </h3>
                  <GaugeIcon size={16} className="text-gray-400" data-id="element-2128" />
                </div>
                <div className="flex justify-center py-4" data-id="element-2129">
                  <GaugeChart value={45} max={100} color="#3b82f6" darkMode={true} size="md" data-id="element-2130" />
                </div>
                <div className="mt-2 text-xs text-gray-400 text-center" data-id="element-2131">
                  <div className="flex justify-between" data-id="element-2132">
                    <span data-id="element-2133">Threads: 32</span>
                    <span data-id="element-2134">Load: 45%</span>
                    <span data-id="element-2135">Temp: 65°C</span>
                  </div>
                </div>
              </div>
              {/* Memory Usage - Bar Chart */}
              <div className="bg-[#0F0F0F] rounded-xl p-4 border border-[#222]" data-id="element-2136">
                <div className="flex items-center justify-between mb-2" data-id="element-2137">
                  <h3 className="text-base font-medium text-white flex items-center gap-2" data-id="element-2138">
                    <MemoryStickIcon size={16} className="text-green-400" data-id="element-2139" />{' '}
                    Memory Usage
                  </h3>
                  <BarChart4Icon size={16} className="text-gray-400" data-id="element-2140" />
                </div>
                <div className="space-y-4 mt-4" data-id="element-2141">
                  <div data-id="element-2142">
                    <div className="flex items-center justify-between mb-1" data-id="element-2143">
                      <span className="text-xs text-gray-400" data-id="element-2144">System</span>
                      <span className="text-xs text-gray-300" data-id="element-2145">18.2 GB</span>
                    </div>
                    <ProgressBar value={45} max={100} color="blue" size="sm" data-id="element-2146" />
                  </div>
                  <div data-id="element-2147">
                    <div className="flex items-center justify-between mb-1" data-id="element-2148">
                      <span className="text-xs text-gray-400" data-id="element-2149">LLM Models</span>
                      <span className="text-xs text-gray-300" data-id="element-2150">24.8 GB</span>
                    </div>
                    <ProgressBar value={62} max={100} color="green" size="sm" data-id="element-2151" />
                  </div>
                  <div data-id="element-2152">
                    <div className="flex items-center justify-between mb-1" data-id="element-2153">
                      <span className="text-xs text-gray-400" data-id="element-2154">
                        Applications
                      </span>
                      <span className="text-xs text-gray-300" data-id="element-2155">6.4 GB</span>
                    </div>
                    <ProgressBar value={16} max={100} color="yellow" size="sm" data-id="element-2156" />
                  </div>
                </div>
                <div className="mt-4 pt-2 border-t border-[#222]" data-id="element-2157">
                  <div className="flex justify-between text-xs" data-id="element-2158">
                    <span className="text-gray-400" data-id="element-2159">Total:</span>
                    <span className="text-white" data-id="element-2160">49.4 GB / 80 GB (62%)</span>
                  </div>
                </div>
              </div>
              {/* GPU Utilization - Line Chart */}
              <div className="bg-[#0F0F0F] rounded-xl p-4 border border-[#222]" data-id="element-2161">
                <div className="flex items-center justify-between mb-2" data-id="element-2162">
                  <h3 className="text-base font-medium text-white flex items-center gap-2" data-id="element-2163">
                    <ServerIcon size={16} className="text-purple-400" data-id="element-2164" /> GPU
                    Utilization
                  </h3>
                  <LineChartIcon size={16} className="text-gray-400" data-id="element-2165" />
                </div>
                <div className="h-40 mt-2" data-id="element-2166">
                  <LineChart data={gpuData.slice(-20)} height={160} color="#a855f7" darkMode={true} showLabels={true} showGrid={true} data-id="element-2167" />
                </div>
                <div className="mt-2 text-xs text-gray-400 text-center" data-id="element-2168">
                  <div className="flex justify-between" data-id="element-2169">
                    <span data-id="element-2170">VRAM: 78%</span>
                    <span data-id="element-2171">Compute: 82%</span>
                    <span data-id="element-2172">Temp: 72°C</span>
                  </div>
                </div>
              </div>
            </div>
            {/* Network I/O */}
            <div className="bg-[#0F0F0F] rounded-xl p-4 border border-[#222]" data-id="element-2173">
              <div className="flex items-center justify-between mb-2" data-id="element-2174">
                <h3 className="text-base font-medium text-white flex items-center gap-2" data-id="element-2175">
                  <NetworkIcon size={16} className="text-yellow-400" data-id="element-2176" /> Network
                  I/O
                </h3>
                <LineChartIcon size={16} className="text-gray-400" data-id="element-2177" />
              </div>
              <div className="h-40 mt-2" data-id="element-2178">
                <LineChart data={networkData.slice(-20)} height={160} color="#eab308" darkMode={true} showLabels={true} showGrid={true} data-id="element-2179" />
              </div>
              <div className="grid grid-cols-3 gap-4 mt-4" data-id="element-2180">
                <div className="bg-[#121212] p-3 rounded-lg border border-[#333] text-center" data-id="element-2181">
                  <div className="text-xs text-gray-400 mb-1" data-id="element-2182">Inbound</div>
                  <div className="text-lg font-semibold text-white" data-id="element-2183">
                    22 MB/s
                  </div>
                </div>
                <div className="bg-[#121212] p-3 rounded-lg border border-[#333] text-center" data-id="element-2184">
                  <div className="text-xs text-gray-400 mb-1" data-id="element-2185">Outbound</div>
                  <div className="text-lg font-semibold text-white" data-id="element-2186">
                    13 MB/s
                  </div>
                </div>
                <div className="bg-[#121212] p-3 rounded-lg border border-[#333] text-center" data-id="element-2187">
                  <div className="text-xs text-gray-400 mb-1" data-id="element-2188">Total</div>
                  <div className="text-lg font-semibold text-white" data-id="element-2189">
                    35 MB/s
                  </div>
                </div>
              </div>
            </div>
          </div>}
        {/* MODELS TAB */}
        {activeTab === 'models' && <div className="space-y-6" data-id="element-2190">
            {/* Active Models */}
            <div className="bg-[#0F0F0F] rounded-xl p-5 border border-[#222]" data-id="element-2191">
              <div className="flex items-center justify-between mb-4" data-id="element-2192">
                <h3 className="text-lg font-medium text-white flex items-center gap-2" data-id="element-2193">
                  <DatabaseIcon size={18} className="text-blue-400" data-id="element-2194" />
                  Active Models
                </h3>
                <div className="flex items-center gap-2" data-id="element-2195">
                  <button className="p-1.5 text-gray-400 hover:text-white bg-[#121212] rounded-lg border border-[#333] hover:bg-[#1a1a1a]" data-id="element-2196">
                    <FilterIcon size={16} data-id="element-2197" />
                  </button>
                  <button className="p-1.5 text-gray-400 hover:text-white bg-[#121212] rounded-lg border border-[#333] hover:bg-[#1a1a1a]" data-id="element-2198">
                    <DownloadIcon size={16} data-id="element-2199" />
                  </button>
                </div>
              </div>
              <div className="overflow-x-auto" data-id="element-2200">
                <table className="min-w-full divide-y divide-[#222]" data-id="element-2201">
                  <thead data-id="element-2202">
                    <tr className="bg-[#121212]" data-id="element-2203">
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider" data-id="element-2204">
                        Model
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider" data-id="element-2205">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider" data-id="element-2206">
                        Usage
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider" data-id="element-2207">
                        Requests/min
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider" data-id="element-2208">
                        Avg. Response
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#222] bg-[#0F0F0F]" data-id="element-2209">
                    <tr className="hover:bg-[#121212] transition-colors" data-id="element-2210">
                      <td className="px-4 py-3 text-sm text-white" data-id="element-2211">
                        <div className="flex items-center" data-id="element-2212">
                          <div className="h-2 w-2 bg-green-500 rounded-full mr-2" data-id="element-2213"></div>
                          Llama-4-8B
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm" data-id="element-2214">
                        <span className="px-2 py-0.5 text-xs bg-green-900/30 text-green-400 rounded-full border border-green-900/50" data-id="element-2215">
                          Active
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-white" data-id="element-2216">
                        <div className="flex items-center" data-id="element-2217">
                          <div className="w-24 mr-2" data-id="element-2218">
                            <ProgressBar value={35} max={100} color="blue" size="sm" data-id="element-2219" />
                          </div>
                          <span data-id="element-2220">35%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-white" data-id="element-2221">1.2K</td>
                      <td className="px-4 py-3 text-sm text-white" data-id="element-2222">120ms</td>
                    </tr>
                    <tr className="hover:bg-[#121212] transition-colors" data-id="element-2223">
                      <td className="px-4 py-3 text-sm text-white" data-id="element-2224">
                        <div className="flex items-center" data-id="element-2225">
                          <div className="h-2 w-2 bg-green-500 rounded-full mr-2" data-id="element-2226"></div>
                          DeepSeek-V3
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm" data-id="element-2227">
                        <span className="px-2 py-0.5 text-xs bg-green-900/30 text-green-400 rounded-full border border-green-900/50" data-id="element-2228">
                          Active
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-white" data-id="element-2229">
                        <div className="flex items-center" data-id="element-2230">
                          <div className="w-24 mr-2" data-id="element-2231">
                            <ProgressBar value={25} max={100} color="purple" size="sm" data-id="element-2232" />
                          </div>
                          <span data-id="element-2233">25%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-white" data-id="element-2234">850</td>
                      <td className="px-4 py-3 text-sm text-white" data-id="element-2235">145ms</td>
                    </tr>
                    <tr className="hover:bg-[#121212] transition-colors" data-id="element-2236">
                      <td className="px-4 py-3 text-sm text-white" data-id="element-2237">
                        <div className="flex items-center" data-id="element-2238">
                          <div className="h-2 w-2 bg-green-500 rounded-full mr-2" data-id="element-2239"></div>
                          Gemma-3
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm" data-id="element-2240">
                        <span className="px-2 py-0.5 text-xs bg-green-900/30 text-green-400 rounded-full border border-green-900/50" data-id="element-2241">
                          Active
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-white" data-id="element-2242">
                        <div className="flex items-center" data-id="element-2243">
                          <div className="w-24 mr-2" data-id="element-2244">
                            <ProgressBar value={20} max={100} color="green" size="sm" data-id="element-2245" />
                          </div>
                          <span data-id="element-2246">20%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-white" data-id="element-2247">620</td>
                      <td className="px-4 py-3 text-sm text-white" data-id="element-2248">95ms</td>
                    </tr>
                    <tr className="hover:bg-[#121212] transition-colors" data-id="element-2249">
                      <td className="px-4 py-3 text-sm text-white" data-id="element-2250">
                        <div className="flex items-center" data-id="element-2251">
                          <div className="h-2 w-2 bg-green-500 rounded-full mr-2" data-id="element-2252"></div>
                          Mistral-3
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm" data-id="element-2253">
                        <span className="px-2 py-0.5 text-xs bg-green-900/30 text-green-400 rounded-full border border-green-900/50" data-id="element-2254">
                          Active
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-white" data-id="element-2255">
                        <div className="flex items-center" data-id="element-2256">
                          <div className="w-24 mr-2" data-id="element-2257">
                            <ProgressBar value={15} max={100} color="yellow" size="sm" data-id="element-2258" />
                          </div>
                          <span data-id="element-2259">15%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-white" data-id="element-2260">480</td>
                      <td className="px-4 py-3 text-sm text-white" data-id="element-2261">110ms</td>
                    </tr>
                    <tr className="hover:bg-[#121212] transition-colors" data-id="element-2262">
                      <td className="px-4 py-3 text-sm text-white" data-id="element-2263">
                        <div className="flex items-center" data-id="element-2264">
                          <div className="h-2 w-2 bg-gray-500 rounded-full mr-2" data-id="element-2265"></div>
                          Llama-3-8B
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm" data-id="element-2266">
                        <span className="px-2 py-0.5 text-xs bg-gray-800 text-gray-400 rounded-full border border-gray-700" data-id="element-2267">
                          Standby
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-white" data-id="element-2268">
                        <div className="flex items-center" data-id="element-2269">
                          <div className="w-24 mr-2" data-id="element-2270">
                            <ProgressBar value={0} max={100} color="gray" size="sm" data-id="element-2271" />
                          </div>
                          <span data-id="element-2272">0%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-white" data-id="element-2273">0</td>
                      <td className="px-4 py-3 text-sm text-white" data-id="element-2274">-</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            {/* Model Usage Distribution */}
            <div className="bg-[#0F0F0F] rounded-xl p-4 border border-[#222]" data-id="element-2275">
              <h3 className="text-base font-medium text-white mb-4 flex items-center gap-2" data-id="element-2276">
                <PieChartIcon size={16} className="text-indigo-400" data-id="element-2277" />
                Model Usage Distribution
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4" data-id="element-2278">
                {serviceData.map((service, index) => <div key={index} className="bg-[#121212] p-3 rounded-lg border border-[#333]" data-id="element-2279">
                    <div className="flex items-center mb-2" data-id="element-2280">
                      <div className="h-3 w-3 rounded-full mr-2" style={{
                  backgroundColor: service.color
                }} data-id="element-2281"></div>
                      <h4 className="text-sm font-medium text-gray-300" data-id="element-2282">
                        {service.name}
                      </h4>
                    </div>
                    <div className="text-xl font-bold text-white mb-1" data-id="element-2283">
                      {service.value}%
                    </div>
                    <ProgressBar value={service.value} max={100} color={service.color} size="sm" data-id="element-2284" />
                  </div>)}
              </div>
            </div>
            {/* Team & Project Usage */}
            <div className="bg-[#0F0F0F] rounded-xl p-4 border border-[#222]" data-id="element-2285">
              <h3 className="text-base font-medium text-white mb-4 flex items-center gap-2" data-id="element-2286">
                <UsersIcon size={16} className="text-blue-400" data-id="element-2287" />
                Team & Project Usage
              </h3>
              <div className="space-y-4" data-id="element-2288">
                {teamUsage.map(team => <div key={team.id} className="space-y-1" data-id="element-2289">
                    <div className="flex items-center justify-between" data-id="element-2290">
                      <span className="text-sm text-white" data-id="element-2291">{team.name}</span>
                      <span className="text-sm text-gray-300" data-id="element-2292">
                        {team.usage}/{team.quota} tokens (millions)
                      </span>
                    </div>
                    <ProgressBar value={team.usage} max={team.quota} color={team.usage / team.quota > 0.8 ? 'red' : team.usage / team.quota > 0.6 ? 'yellow' : 'green'} size="md" animated={team.usage / team.quota > 0.9} data-id="element-2293" />
                  </div>)}
              </div>
            </div>
          </div>}
        {/* API TAB */}
        {activeTab === 'api' && <div className="space-y-6" data-id="element-2294">
            {/* API Request Summary */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-2295">
              <div className="bg-[#0F0F0F] rounded-xl p-4 border border-[#222]" data-id="element-2296">
                <h3 className="text-sm font-medium text-gray-400 mb-2" data-id="element-2297">
                  Total Requests
                </h3>
                <p className="text-2xl font-semibold text-white" data-id="element-2298">3,245/min</p>
                <span className="text-green-400 text-xs flex items-center mt-1" data-id="element-2299">
                  <ArrowUpIcon size={12} className="mr-1" data-id="element-2300" />
                  12% from last hour
                </span>
              </div>
              <div className="bg-[#0F0F0F] rounded-xl p-4 border border-[#222]" data-id="element-2301">
                <h3 className="text-sm font-medium text-gray-400 mb-2" data-id="element-2302">
                  Avg. Response Time
                </h3>
                <p className="text-2xl font-semibold text-white" data-id="element-2303">120ms</p>
                <span className="text-green-400 text-xs flex items-center mt-1" data-id="element-2304">
                  <ArrowDownIcon size={12} className="mr-1" data-id="element-2305" />
                  5% from last hour
                </span>
              </div>
              <div className="bg-[#0F0F0F] rounded-xl p-4 border border-[#222]" data-id="element-2306">
                <h3 className="text-sm font-medium text-gray-400 mb-2" data-id="element-2307">
                  Error Rate
                </h3>
                <p className="text-2xl font-semibold text-white" data-id="element-2308">0.02%</p>
                <span className="text-green-400 text-xs flex items-center mt-1" data-id="element-2309">
                  <ArrowDownIcon size={12} className="mr-1" data-id="element-2310" />
                  0.01% from last hour
                </span>
              </div>
              <div className="bg-[#0F0F0F] rounded-xl p-4 border border-[#222]" data-id="element-2311">
                <h3 className="text-sm font-medium text-gray-400 mb-2" data-id="element-2312">
                  Active Connections
                </h3>
                <p className="text-2xl font-semibold text-white" data-id="element-2313">142</p>
                <span className="text-green-400 text-xs flex items-center mt-1" data-id="element-2314">
                  <ArrowUpIcon size={12} className="mr-1" data-id="element-2315" />
                  8% from last hour
                </span>
              </div>
            </div>
            {/* API Request Trend */}
            <div className="bg-[#0F0F0F] rounded-xl p-4 border border-[#222]" data-id="element-2316">
              <div className="flex items-center justify-between mb-2" data-id="element-2317">
                <h3 className="text-base font-medium text-white flex items-center gap-2" data-id="element-2318">
                  <ActivityIcon size={16} className="text-blue-400" data-id="element-2319" /> API
                  Request Trend
                </h3>
                <div className="flex items-center gap-2" data-id="element-2320">
                  <button className="p-1.5 text-gray-400 hover:text-white bg-[#121212] rounded-lg border border-[#333] hover:bg-[#1a1a1a]" data-id="element-2321">
                    <DownloadIcon size={16} data-id="element-2322" />
                  </button>
                </div>
              </div>
              <div className="h-60 mt-2" data-id="element-2323">
                <LineChart data={systemData} height={240} color="#3b82f6" darkMode={true} showLabels={true} showGrid={true} data-id="element-2324" />
              </div>
            </div>
            {/* API Request Distribution */}
            <div className="bg-[#0F0F0F] rounded-xl p-4 border border-[#222]" data-id="element-2325">
              <h3 className="text-base font-medium text-white mb-4 flex items-center gap-2" data-id="element-2326">
                <PieChartIcon size={16} className="text-blue-400" data-id="element-2327" />
                API Request Distribution
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-2328">
                {apiRequestData.map((api, index) => <div key={index} className="bg-[#121212] p-3 rounded-lg border border-[#333]" data-id="element-2329">
                    <div className="flex items-center mb-2" data-id="element-2330">
                      <div className="h-3 w-3 rounded-full mr-2" style={{
                  backgroundColor: api.color
                }} data-id="element-2331"></div>
                      <h4 className="text-sm font-medium text-gray-300" data-id="element-2332">
                        {api.name}
                      </h4>
                    </div>
                    <div className="text-xl font-bold text-white mb-1" data-id="element-2333">
                      {api.value}%
                    </div>
                    <ProgressBar value={api.value} max={100} color={api.color} size="sm" data-id="element-2334" />
                  </div>)}
              </div>
            </div>
            {/* Resource Usage By Service */}
            <div className="bg-[#0F0F0F] rounded-xl p-4 border border-[#222]" data-id="element-2335">
              <div className="flex items-center justify-between mb-4" data-id="element-2336">
                <h3 className="text-base font-medium text-white flex items-center gap-2" data-id="element-2337">
                  <ServerIcon size={16} className="text-purple-400" data-id="element-2338" />
                  Resource Usage By Service
                </h3>
                <div className="flex items-center gap-2" data-id="element-2339">
                  <button className="p-1.5 text-gray-400 hover:text-white bg-[#121212] rounded-lg border border-[#333] hover:bg-[#1a1a1a]" data-id="element-2340">
                    <FilterIcon size={16} data-id="element-2341" />
                  </button>
                </div>
              </div>
              <div className="overflow-x-auto" data-id="element-2342">
                <table className="min-w-full divide-y divide-[#222]" data-id="element-2343">
                  <thead data-id="element-2344">
                    <tr className="bg-[#121212]" data-id="element-2345">
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider" data-id="element-2346">
                        Service
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider" data-id="element-2347">
                        CPU
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider" data-id="element-2348">
                        Memory
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider" data-id="element-2349">
                        GPU
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider" data-id="element-2350">
                        Requests
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#222] bg-[#0F0F0F]" data-id="element-2351">
                    <tr className="hover:bg-[#121212] transition-colors" data-id="element-2352">
                      <td className="px-4 py-3 text-sm text-white" data-id="element-2353">
                        Llama-4-8B
                      </td>
                      <td className="px-4 py-3 text-sm text-white" data-id="element-2354">35%</td>
                      <td className="px-4 py-3 text-sm text-white" data-id="element-2355">42%</td>
                      <td className="px-4 py-3 text-sm text-white" data-id="element-2356">65%</td>
                      <td className="px-4 py-3 text-sm text-white" data-id="element-2357">1.2K/min</td>
                    </tr>
                    <tr className="hover:bg-[#121212] transition-colors" data-id="element-2358">
                      <td className="px-4 py-3 text-sm text-white" data-id="element-2359">
                        DeepSeek-V3
                      </td>
                      <td className="px-4 py-3 text-sm text-white" data-id="element-2360">28%</td>
                      <td className="px-4 py-3 text-sm text-white" data-id="element-2361">35%</td>
                      <td className="px-4 py-3 text-sm text-white" data-id="element-2362">72%</td>
                      <td className="px-4 py-3 text-sm text-white" data-id="element-2363">850/min</td>
                    </tr>
                    <tr className="hover:bg-[#121212] transition-colors" data-id="element-2364">
                      <td className="px-4 py-3 text-sm text-white" data-id="element-2365">Gemma-3</td>
                      <td className="px-4 py-3 text-sm text-white" data-id="element-2366">18%</td>
                      <td className="px-4 py-3 text-sm text-white" data-id="element-2367">22%</td>
                      <td className="px-4 py-3 text-sm text-white" data-id="element-2368">45%</td>
                      <td className="px-4 py-3 text-sm text-white" data-id="element-2369">620/min</td>
                    </tr>
                    <tr className="hover:bg-[#121212] transition-colors" data-id="element-2370">
                      <td className="px-4 py-3 text-sm text-white" data-id="element-2371">
                        Mistral-3
                      </td>
                      <td className="px-4 py-3 text-sm text-white" data-id="element-2372">12%</td>
                      <td className="px-4 py-3 text-sm text-white" data-id="element-2373">18%</td>
                      <td className="px-4 py-3 text-sm text-white" data-id="element-2374">38%</td>
                      <td className="px-4 py-3 text-sm text-white" data-id="element-2375">480/min</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>}
        {/* ALERTS TAB */}
        {activeTab === 'alerts' && <div className="space-y-6" data-id="element-2376">
            {/* Alert Summary */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-2377">
              <div className="bg-[#0F0F0F] rounded-xl p-4 border border-[#222]" data-id="element-2378">
                <h3 className="text-sm font-medium text-gray-400 mb-2" data-id="element-2379">
                  Total Alerts
                </h3>
                <p className="text-2xl font-semibold text-white" data-id="element-2380">24</p>
                <span className="text-green-400 text-xs flex items-center mt-1" data-id="element-2381">
                  <ArrowDownIcon size={12} className="mr-1" data-id="element-2382" />3 from yesterday
                </span>
              </div>
              <div className="bg-[#0F0F0F] rounded-xl p-4 border border-[#222]" data-id="element-2383">
                <h3 className="text-sm font-medium text-gray-400 mb-2" data-id="element-2384">
                  Critical Alerts
                </h3>
                <p className="text-2xl font-semibold text-red-400" data-id="element-2385">2</p>
                <span className="text-green-400 text-xs flex items-center mt-1" data-id="element-2386">
                  <ArrowDownIcon size={12} className="mr-1" data-id="element-2387" />1 from yesterday
                </span>
              </div>
              <div className="bg-[#0F0F0F] rounded-xl p-4 border border-[#222]" data-id="element-2388">
                <h3 className="text-sm font-medium text-gray-400 mb-2" data-id="element-2389">
                  Warnings
                </h3>
                <p className="text-2xl font-semibold text-yellow-400" data-id="element-2390">8</p>
                <span className="text-yellow-400 text-xs flex items-center mt-1" data-id="element-2391">
                  <MoveHorizontalIcon size={12} className="mr-1" data-id="element-2392" />
                  No change from yesterday
                </span>
              </div>
              <div className="bg-[#0F0F0F] rounded-xl p-4 border border-[#222]" data-id="element-2393">
                <h3 className="text-sm font-medium text-gray-400 mb-2" data-id="element-2394">Info</h3>
                <p className="text-2xl font-semibold text-blue-400" data-id="element-2395">14</p>
                <span className="text-red-400 text-xs flex items-center mt-1" data-id="element-2396">
                  <ArrowUpIcon size={12} className="mr-1" data-id="element-2397" />2 from yesterday
                </span>
              </div>
            </div>
            {/* Recent Alerts */}
            <div className="bg-[#0F0F0F] rounded-xl p-4 border border-[#222]" data-id="element-2398">
              <div className="flex items-center justify-between mb-4" data-id="element-2399">
                <h3 className="text-base font-medium text-white flex items-center gap-2" data-id="element-2400">
                  <BellIcon size={16} className="text-red-400" data-id="element-2401" />
                  Recent Alerts
                </h3>
                <div className="flex items-center gap-2" data-id="element-2402">
                  <button className="p-1.5 text-gray-400 hover:text-white bg-[#121212] rounded-lg border border-[#333] hover:bg-[#1a1a1a]" data-id="element-2403">
                    <FilterIcon size={16} data-id="element-2404" />
                  </button>
                </div>
              </div>
              <div className="space-y-3" data-id="element-2405">
                <div className="bg-red-900/20 border border-red-900/30 rounded-lg p-3 flex items-start" data-id="element-2406">
                  <div className="h-2 w-2 bg-red-500 rounded-full mt-1.5 mr-3" data-id="element-2407"></div>
                  <div className="flex-1" data-id="element-2408">
                    <div className="flex items-center justify-between" data-id="element-2409">
                      <h4 className="text-sm font-medium text-white" data-id="element-2410">
                        GPU 2 temperature exceeding threshold
                      </h4>
                      <span className="text-xs text-gray-400" data-id="element-2411">2 min ago</span>
                    </div>
                    <p className="text-xs text-gray-300 mt-1" data-id="element-2412">
                      Temperature reached 85°C, exceeding the 80°C threshold.
                      Consider reducing workload or checking cooling systems.
                    </p>
                  </div>
                </div>
                <div className="bg-yellow-900/20 border border-yellow-900/30 rounded-lg p-3 flex items-start" data-id="element-2413">
                  <div className="h-2 w-2 bg-yellow-500 rounded-full mt-1.5 mr-3" data-id="element-2414"></div>
                  <div className="flex-1" data-id="element-2415">
                    <div className="flex items-center justify-between" data-id="element-2416">
                      <h4 className="text-sm font-medium text-white" data-id="element-2417">
                        API rate limit reached for key ABC123
                      </h4>
                      <span className="text-xs text-gray-400" data-id="element-2418">15 min ago</span>
                    </div>
                    <p className="text-xs text-gray-300 mt-1" data-id="element-2419">
                      Rate limit of 1000 requests/min has been reached. Consider
                      upgrading your plan or optimizing request patterns.
                    </p>
                  </div>
                </div>
                <div className="bg-blue-900/20 border border-blue-900/30 rounded-lg p-3 flex items-start" data-id="element-2420">
                  <div className="h-2 w-2 bg-blue-500 rounded-full mt-1.5 mr-3" data-id="element-2421"></div>
                  <div className="flex-1" data-id="element-2422">
                    <div className="flex items-center justify-between" data-id="element-2423">
                      <h4 className="text-sm font-medium text-white" data-id="element-2424">
                        Model swap completed: Llama-4-8B
                      </h4>
                      <span className="text-xs text-gray-400" data-id="element-2425">45 min ago</span>
                    </div>
                    <p className="text-xs text-gray-300 mt-1" data-id="element-2426">
                      Model Llama-4-8B has been successfully loaded and is now
                      available for inference.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            {/* Activity Log */}
            <div className="bg-[#0F0F0F] rounded-xl p-4 border border-[#222]" data-id="element-2427">
              <div className="flex items-center justify-between mb-4" data-id="element-2428">
                <h3 className="text-base font-medium text-white flex items-center gap-2" data-id="element-2429">
                  <HistoryIcon size={16} className="text-blue-400" data-id="element-2430" />
                  Activity Log
                </h3>
                <div className="flex items-center gap-2" data-id="element-2431">
                  <button className="px-2.5 py-1 text-xs text-gray-300 hover:text-white bg-[#121212] rounded-lg border border-[#333] hover:bg-[#1a1a1a]" data-id="element-2432">
                    View All
                  </button>
                </div>
              </div>
              <div className="space-y-4" data-id="element-2433">
                <div className="flex items-start" data-id="element-2434">
                  <div className="flex-shrink-0 mr-3" data-id="element-2435">
                    <ClockIcon size={14} className="text-gray-400 mt-1" data-id="element-2436" />
                  </div>
                  <div data-id="element-2437">
                    <p className="text-sm text-gray-200" data-id="element-2438">
                      Model swap: Llama-4-8B loaded
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-2439">2 min ago</p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-2440">
                  <div className="flex-shrink-0 mr-3" data-id="element-2441">
                    <ClockIcon size={14} className="text-gray-400 mt-1" data-id="element-2442" />
                  </div>
                  <div data-id="element-2443">
                    <p className="text-sm text-gray-200" data-id="element-2444">
                      API rate limit increased for team: Research
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-2445">15 min ago</p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-2446">
                  <div className="flex-shrink-0 mr-3" data-id="element-2447">
                    <ClockIcon size={14} className="text-gray-400 mt-1" data-id="element-2448" />
                  </div>
                  <div data-id="element-2449">
                    <p className="text-sm text-gray-200" data-id="element-2450">
                      System update completed
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-2451">45 min ago</p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-2452">
                  <div className="flex-shrink-0 mr-3" data-id="element-2453">
                    <ClockIcon size={14} className="text-gray-400 mt-1" data-id="element-2454" />
                  </div>
                  <div data-id="element-2455">
                    <p className="text-sm text-gray-200" data-id="element-2456">
                      New model added to standby pool: Mistral-3
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-2457">2 hours ago</p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-2458">
                  <div className="flex-shrink-0 mr-3" data-id="element-2459">
                    <ClockIcon size={14} className="text-gray-400 mt-1" data-id="element-2460" />
                  </div>
                  <div data-id="element-2461">
                    <p className="text-sm text-gray-200" data-id="element-2462">
                      Scheduled maintenance completed
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-2463">3 hours ago</p>
                  </div>
                </div>
              </div>
            </div>
          </div>}
      </div>
      {/* CSS for animations and styles */}
      <style jsx global data-id="element-2464">{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(5px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out forwards;
        }
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>;
}